import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  Tab,
  Tabs,
  Avatar,
  LinearProgress,
  TablePagination,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import { qcStationApi, QcQueueItem, QcInspection } from '../api/qcStation';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function MaterialQcPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState(0);

  // Queue state
  const [queue, setQueue] = useState<QcQueueItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);

  // History state
  const [history, setHistory] = useState<QcInspection[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(25);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Inspection dialog state
  const [inspectItem, setInspectItem] = useState<QcQueueItem | null>(null);
  const [passedPieces, setPassedPieces] = useState('');
  const [failedPieces, setFailedPieces] = useState('0');
  const [repairPieces, setRepairPieces] = useState('0');
  const [inspectNotes, setInspectNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'success',
  });

  const fetchQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const data = await qcStationApi.getQueue();
      setQueue(data.items);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load QC queue', severity: 'error' });
    } finally {
      setQueueLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await qcStationApi.getHistory(historyPage + 1, historyRowsPerPage);
      setHistory(data.inspections);
      setHistoryTotal(data.total);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load history', severity: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyRowsPerPage]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);
  useEffect(() => { if (tab === 1) fetchHistory(); }, [tab, fetchHistory]);

  const openInspect = (item: QcQueueItem) => {
    setInspectItem(item);
    setPassedPieces(String(item.available_qty));
    setFailedPieces('0');
    setRepairPieces('0');
    setInspectNotes('');
  };

  const totalInspected = (Number(passedPieces) || 0) + (Number(failedPieces) || 0) + (Number(repairPieces) || 0);
  const inspectValid =
    inspectItem !== null &&
    totalInspected > 0 &&
    totalInspected <= (inspectItem?.available_qty ?? 0) &&
    (Number(passedPieces) || 0) >= 0 &&
    (Number(failedPieces) || 0) >= 0 &&
    (Number(repairPieces) || 0) >= 0;

  const handleInspect = async () => {
    if (!inspectItem || !inspectValid) return;
    setSubmitting(true);
    try {
      const result = await qcStationApi.inspect(inspectItem.id, {
        passed_pieces: Number(passedPieces) || 0,
        failed_pieces: Number(failedPieces) || 0,
        repair_pieces: Number(repairPieces) || 0,
        notes: inspectNotes || undefined,
      });
      setSnackbar({ open: true, message: result.message, severity: 'success' });
      setInspectItem(null);
      fetchQueue();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Inspection failed', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <FactCheckIcon color="primary" sx={{ fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Material QC Station</Typography>
          <Typography variant="body2" color="text.secondary">
            Inspect semi-finished items received from outside job works
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={fetchQueue} size="small">Refresh</Button>
      </Box>

      {/* Summary chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          icon={<FactCheckIcon />}
          label={`${queue.length} Pending Inspection`}
          color={queue.length > 0 ? 'warning' : 'default'}
          variant={queue.length > 0 ? 'filled' : 'outlined'}
        />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
        <Tab label="QC Queue" icon={<FactCheckIcon />} iconPosition="start" />
        <Tab label="History" icon={<HistoryIcon />} iconPosition="start" />
      </Tabs>

      {/* QC Queue Tab */}
      <TabPanel value={tab} index={0}>
        {queueLoading ? (
          <LinearProgress />
        ) : queue.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">QC queue is clear!</Typography>
            <Typography variant="body2" color="text.secondary">
              No semi-finished items awaiting inspection.
            </Typography>
          </Paper>
        ) : isMobile ? (
          // Mobile card view
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {queue.map((item) => (
              <Card key={item.id} variant="outlined">
                <CardContent sx={{ pb: '12px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    {item.image_url && (
                      <Avatar src={item.image_url} variant="rounded" sx={{ width: 40, height: 40 }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>{item.design_no}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.garment_part} {item.size ? `· ${item.size}` : ''}
                      </Typography>
                    </Box>
                    <Chip label={`${item.available_qty} pcs`} color="warning" size="small" />
                  </Box>
                  {item.source_job_work && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      From: {item.source_job_work.jobWorkCode} · {item.source_job_work.vendor.alias || item.source_job_work.vendor.registered_name}
                    </Typography>
                  )}
                  <Button fullWidth variant="contained" size="small" onClick={() => openInspect(item)}>
                    Inspect
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          // Desktop table view
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Design</TableCell>
                    <TableCell>Part / Size</TableCell>
                    <TableCell align="right">Pending Qty</TableCell>
                    <TableCell>Source JW</TableCell>
                    <TableCell>Vendor</TableCell>
                    <TableCell>Received On</TableCell>
                    <TableCell align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queue.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.image_url && (
                            <Avatar src={item.image_url} variant="rounded" sx={{ width: 32, height: 32 }} />
                          )}
                          <Typography variant="body2" fontWeight={500}>{item.design_no || item.material_code}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {item.garment_part}
                        {item.size && <Chip label={item.size} size="small" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell align="right">
                        <Chip label={`${item.available_qty} pcs`} color="warning" size="small" />
                      </TableCell>
                      <TableCell>{item.source_job_work?.jobWorkCode || '—'}</TableCell>
                      <TableCell>
                        {item.source_job_work
                          ? item.source_job_work.vendor.alias || item.source_job_work.vendor.registered_name
                          : '—'}
                      </TableCell>
                      <TableCell>
                        {new Date(item.created_at).toLocaleDateString('en-IN')}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => openInspect(item)}
                        >
                          Inspect
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </TabPanel>

      {/* History Tab */}
      <TabPanel value={tab} index={1}>
        {historyLoading ? (
          <LinearProgress />
        ) : (
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Design / Material</TableCell>
                    <TableCell>Part / Size</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Passed</TableCell>
                    <TableCell align="right">Perm. Damage</TableCell>
                    <TableCell align="right">Repair</TableCell>
                    <TableCell>Inspected By</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No inspection history yet
                      </TableCell>
                    </TableRow>
                  ) : history.map((insp) => (
                    <TableRow key={insp.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {insp.material_inward?.image_url && (
                            <Avatar src={insp.material_inward.image_url} variant="rounded" sx={{ width: 28, height: 28 }} />
                          )}
                          <Typography variant="body2">
                            {insp.design_no || insp.material_inward?.material_code || '—'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {insp.garment_part}
                        {insp.size && <Chip label={insp.size} size="small" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell align="right">{insp.total_pieces}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main" fontWeight={600}>
                          {insp.passed_pieces}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {insp.failed_pieces > 0 ? (
                          <Typography variant="body2" color="error.main" fontWeight={600}>
                            {insp.failed_pieces}
                          </Typography>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        {insp.repair_pieces > 0 ? (
                          <Typography variant="body2" color="warning.main" fontWeight={600}>
                            {insp.repair_pieces}
                          </Typography>
                        ) : '—'}
                      </TableCell>
                      <TableCell>{insp.inspected_by || '—'}</TableCell>
                      <TableCell>{new Date(insp.inspected_at).toLocaleDateString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={historyTotal}
              page={historyPage}
              onPageChange={(_, p) => setHistoryPage(p)}
              rowsPerPage={historyRowsPerPage}
              onRowsPerPageChange={(e) => { setHistoryRowsPerPage(Number(e.target.value)); setHistoryPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Paper>
        )}
      </TabPanel>

      {/* Inspection Dialog */}
      <Dialog open={!!inspectItem} onClose={() => !submitting && setInspectItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FactCheckIcon color="primary" />
            <Box>
              <Typography variant="h6">QC Inspection</Typography>
              <Typography variant="body2" color="text.secondary">
                {inspectItem?.design_no} · {inspectItem?.garment_part} {inspectItem?.size ? `· ${inspectItem.size}` : ''}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {inspectItem && (
            <Box>
              {/* Info row */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Source JW</Typography>
                  <Typography variant="body2">{inspectItem.source_job_work?.jobWorkCode || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Vendor</Typography>
                  <Typography variant="body2">
                    {inspectItem.source_job_work
                      ? inspectItem.source_job_work.vendor.alias || inspectItem.source_job_work.vendor.registered_name
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Available Qty</Typography>
                  <Typography variant="body2" fontWeight={700}>{inspectItem.available_qty} pcs</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Inspection Result</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Passed ✓"
                    type="number"
                    fullWidth
                    value={passedPieces}
                    onChange={(e) => setPassedPieces(e.target.value)}
                    inputProps={{ min: 0, max: inspectItem.available_qty }}
                    color="success"
                    helperText="Moves to Finished inventory"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Repair ↺"
                    type="number"
                    fullWidth
                    value={repairPieces}
                    onChange={(e) => setRepairPieces(e.target.value)}
                    inputProps={{ min: 0 }}
                    color="warning"
                    helperText="Returns to jobber for rework"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Permanent Damage ✗"
                    type="number"
                    fullWidth
                    value={failedPieces}
                    onChange={(e) => setFailedPieces(e.target.value)}
                    inputProps={{ min: 0 }}
                    color="error"
                    helperText="Irreparable — written off"
                  />
                </Grid>
              </Grid>

              {/* Running total */}
              <Box sx={{ mt: 2, p: 1.5, bgcolor: totalInspected > (inspectItem?.available_qty ?? 0) ? 'error.50' : 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  Total inspected: <strong>{totalInspected}</strong> / {inspectItem.available_qty} pcs
                  {totalInspected > inspectItem.available_qty && (
                    <Typography component="span" color="error.main"> ⚠ exceeds available</Typography>
                  )}
                </Typography>
              </Box>

              {Number(passedPieces) > 0 && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {passedPieces} piece(s) will be converted to Finished inventory.
                </Alert>
              )}
              {Number(repairPieces) > 0 && inspectItem?.source_job_work && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {repairPieces} piece(s) will be returned to {inspectItem.source_job_work.vendor.alias || inspectItem.source_job_work.vendor.registered_name} for repair.
                </Alert>
              )}
              {Number(failedPieces) > 0 && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {failedPieces} piece(s) will be permanently written off (damaged).
                </Alert>
              )}

              <TextField
                label="Notes (optional)"
                multiline
                rows={2}
                fullWidth
                value={inspectNotes}
                onChange={(e) => setInspectNotes(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInspectItem(null)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleInspect}
            disabled={!inspectValid || submitting}
            color="primary"
          >
            {submitting ? 'Submitting…' : 'Submit Inspection'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
